import { Modal } from '../../../components/modal/modal';
import { Button } from '../../../components/form/button';
import { Order, OrderStatus } from '../../../clients/quipli-service/order/order';
import './status-transitions.scss';
import Icon from '../../../components/icons/icon';
import { SelectForm } from '../../../components/select-form/select-form';
import { TextArea } from '../../../components/textarea/textarea';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { CancelReason } from '../../../clients/quipli-service/cancel-reason/cancel-reasons';
import { toast } from '../../../util/toasts';
import { Spinner } from '../../../components/spinner/spinner';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { PaymentStatus } from '../../../clients/quipli-service/payment/payment';
import { displayMoneyFormat } from '../../../util/util';
import { Location } from '../../../clients/quipli-service/location/location';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';

type PaymentMethodToRefund = {
  methodType: string;
  amount: number;
};

export const RejectOrder = ({
  order,
  location,
  onClose,
  onConfirm,
}: {
  order: Order;
  location: Location;
  onClose: () => void;
  onConfirm?: () => void;
}) => {
  const { cancelOrder } = useOrderApiV1();
  const isQuote = order.status === OrderStatus.Quote;

  return (
    <ActionModal
      order={order}
      location={location}
      title={isQuote ? 'Reject Quote' : 'Cancel Order'}
      additionalText={`Are you sure you want to ${isQuote ? 'reject this quote' : 'cancel this order'}?`}
      headerIcon={<Icon icon="InfoCircle" size="xlarge" stroke="var(--warning-300)" />}
      onConfirm={async (comments: string, doRefund: boolean, reasonId?: number) => {
        if (!reasonId) {
          throw new Error('Reason is required');
        }
        try {
          await cancelOrder(order.id, reasonId, comments, doRefund);
          toast(`${isQuote ? 'Quote' : 'Order'} cancelled!`, 'success');
          onConfirm?.();
        } catch (error) {
          throw new Error(`Failed to ${isQuote ? 'reject quote' : 'cancel order'}!`);
        }
      }}
      onClose={onClose}
    />
  );
};

export const MissedOrder = ({
  order,
  location,
  onClose,
  onConfirm,
}: {
  order: Order;
  location: Location;
  onClose: () => void;
  onConfirm?: () => void;
}) => {
  const { markAsMissedOrder } = useOrderApiV1();

  return (
    <ActionModal
      order={order}
      location={location}
      title="Mark As Missed Rental"
      showReasons={false}
      additionalText="Are you sure you want to mark this order as missed?"
      headerIcon={<Icon icon="InfoCircle" size="xlarge" stroke="var(--blue-gray-500)" />}
      onConfirm={async (comments: string, doRefund: boolean) => {
        try {
          await markAsMissedOrder(order.id, comments, doRefund);
          toast('Order marked as missed!', 'success');
          onConfirm?.();
        } catch (error) {
          throw new Error('Failed to mark order as missed!');
        }
      }}
      onClose={onClose}
    />
  );
};

export const ActionModal = ({
  order,
  location,
  title,
  additionalText,
  headerIcon,
  showReasons = true,
  onClose,
  onConfirm,
}: {
  order: Order;
  location: Location;
  title: string;
  additionalText?: ReactNode;
  headerIcon?: ReactNode;
  showReasons?: boolean;
  onClose: () => void;
  onConfirm?: (comments: string, doRefund: boolean, reasonId?: number) => Promise<void>;
}) => {
  const { listCancelReasons } = useAPI();

  // Action modal state
  const [isActionModalOpen, setIsActionModalOpen] = useState<boolean>(true);
  const [selectedReason, setSelectedReason] = useState<CancelReason | null>(null);
  const [areCommentsRequired, setAreCommentsRequired] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reasons, setReasons] = useState<CancelReason[]>([]);
  const [comments, setComments] = useState<string>('');

  // Refund modal state
  const [isRefundModalOpen, setIsRefundModalOpen] = useState<boolean>(false);
  const [toRefundPayments, setToRefundPayments] = useState<PaymentMethodToRefund[]>([]);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const isDisabled = (showReasons && !selectedReason) || (areCommentsRequired && !comments);

  const handleConfirmAction = async (doRefund: boolean) => {
    if (isDisabled || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm?.(comments, doRefund, selectedReason?.id);
      onClose();
      setIsActionModalOpen(false);
      setIsRefundModalOpen(false);
    } catch (error: any) {
      toast(error.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    onClose();
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      const methodTotal: Record<string, number> = {};
      order.invoices
        .flatMap((inv) => inv.payments)
        .forEach((p) => {
          if (p.status !== PaymentStatus.succeeded || p.method === 'QBO') return;

          if (methodTotal[p.method] !== undefined) {
            methodTotal[p.method] += p.amount;
          } else {
            methodTotal[p.method] = p.amount;
          }
        });

      setToRefundPayments(
        Object.entries(methodTotal)
          .filter(([, amount]) => amount > 0)
          .map(([methodType, amount]) => ({ methodType, amount })),
      );

      showReasons && setReasons(await listCancelReasons());
      setIsLoading(false);
    };

    init();
  }, []);

  useEffect(() => {
    showReasons && setAreCommentsRequired(selectedReason?.title === 'Other');
  }, [selectedReason]);

  return (
    <>
      <Modal
        isOpen={isActionModalOpen}
        title={title}
        headerIcon={headerIcon}
        additionalText={additionalText}
        actions={
          isSubmitting ? (
            <Spinner />
          ) : (
            <>
              <Button className="button-block button-secondary" onClick={handleClose}>
                No
              </Button>

              <Button
                className="button-yellow"
                disabled={isDisabled}
                onClick={() => {
                  if (toRefundPayments.length > 0) {
                    setIsActionModalOpen(false);
                    setIsRefundModalOpen(true);
                  } else handleConfirmAction(false);
                }}
              >
                Yes
              </Button>
            </>
          )
        }
        close={handleClose}
      >
        {showReasons && (
          <div>
            <SelectForm
              label="Reason"
              isRequired
              isLoading={isLoading}
              isSearchable={false}
              options={reasons}
              value={selectedReason}
              getOptionLabel={(op) => op.title}
              getOptionValue={(op) => `${op.id}`}
              onChange={setSelectedReason}
            />
          </div>
        )}

        <div className="w-full">
          <TextArea
            label={`Comments${!areCommentsRequired ? ' (optional)' : ''}`}
            isRequired={areCommentsRequired}
            className="w-full text-sm"
            value={comments}
            onChange={({ target }) => setComments(target.value)}
            autoResize={false}
            rows={5}
          />
        </div>
      </Modal>

      <ConfirmModal
        isOpen={isRefundModalOpen}
        prompt="Refund Order"
        additionalText={
          <>
            <span>Do you want to refund this order?</span>

            {toRefundPayments
              .filter((p) => p.methodType === 'Stripe')
              .map(({ methodType, amount }) => (
                <Fragment key={`refund-payment-${methodType}`}>
                  <br />
                  <span>The following amount will be refunded to the customer:</span>
                  <br />
                  <PaymentAmountToRefund methodType={methodType} amount={amount} currency={location.currency} />
                </Fragment>
              ))}

            {toRefundPayments.find((p) => p.methodType !== 'Stripe') && (
              <>
                <br />
                <span>Make sure to refund the following payments:</span>
                {toRefundPayments
                  .filter((p) => p.methodType !== 'Stripe')
                  .map(({ methodType, amount }) => (
                    <Fragment key={`refund-payment-${methodType}`}>
                      <br />
                      <PaymentAmountToRefund methodType={methodType} amount={amount} currency={location.currency} />
                    </Fragment>
                  ))}
              </>
            )}
          </>
        }
        headerIcon={<Icon icon="InfoCircle" size="xlarge" stroke="var(--warning-300)" />}
        yesButtonYellow
        disableButtons={isSubmitting}
        onConfirm={() => handleConfirmAction(true)}
        close={() => handleConfirmAction(false)}
      />
    </>
  );
};

const PaymentAmountToRefund = ({ methodType, amount, currency }: PaymentMethodToRefund & { currency: string }) => (
  <span>
    {methodType}: {displayMoneyFormat(amount, currency)}
  </span>
);
